import { useQuery } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { createContext, useEffect, useMemo, useState } from 'react';
import { downloadXlsx } from '@/components/Downloads/helpers';
import {
  DEFAULT_TRACE_LIMIT,
  getMaxYFromTrace,
  LEGEND_ONLY,
  TOP_SORT_DIRECTION,
} from '@/components/Graphs/constants';
import GraphControls from '@/components/Graphs/GraphControls';
import { shouldUpdateLegend } from '@/components/Portfolio/Charts/utils';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { titleize } from '@/utils/helpers';
import { GOOGLE_ANALYTICS_TAG } from '../../constants';
import { GRAPH_ANALYTICS } from '../operations';
import {
  ALL,
  COUNTRY_AGGREGATE,
  getFilteredGraphTypeChildren,
  graphTitles,
  HYDROCARBON_CHECKBOX_AGGREGATE,
  INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION,
  lineGraphProductionAggregateTypes,
  lineGraphTypeChildren,
  PDP_AND_PUD_CHECKBOX_AGGREGATE,
  productionGraphTypes,
  TOTAL_PRODUCTION,
} from '../utils';

const ProductionLineGraphContext = createContext();
let useDefaultRange = true;
const visibleTraces = {};
const DEFAULT_X_RANGE = [2010, 2050];

export const ProductionLineGraphProvider = ({ children, portfolios }) => {
  const { portfolioEntitlement: entitlementEnabled } = useGetFeatureFlags();
  const [openControlsModal, setOpenControlsModal] = useState(false);

  const [aggregate, setAggregate] = useState(COUNTRY_AGGREGATE.name);
  const [checkboxAggregates, setCheckboxAggregates] = useState({
    [PDP_AND_PUD_CHECKBOX_AGGREGATE.name]: false,
    [HYDROCARBON_CHECKBOX_AGGREGATE.name]: false,
  });
  const [stacked, setStacked] = useState(true);
  const [graphType, setGraphType] = useState(TOTAL_PRODUCTION);
  // Filter lineGraphTypeChildren based on feature flag
  const filteredLineGraphTypeChildren = useMemo(
    () => getFilteredGraphTypeChildren(lineGraphTypeChildren, entitlementEnabled),
    [entitlementEnabled],
  );
  // Initialize with "Include Non-Modelled Production" checked by default, but not "Entitlement"
  // Note: Initial state filters out Entitlement by default (assumes flag is false initially)
  // This will be updated when the feature flag loads
  const [graphTypeOptions, setGraphTypeOptions] = useState(() => {
    const filtered = getFilteredGraphTypeChildren(lineGraphTypeChildren, false);
    return (filtered[TOTAL_PRODUCTION.value] || []).filter(
      option => option.name === INCLUDE_NON_MODELLED_PRODUCTION_GRAPH_TYPE_OPTION.name,
    );
  });
  const [hydrocarbonType, setHydrocarbonType] = useState(ALL.label);
  const [limit, setLimit] = useState(DEFAULT_TRACE_LIMIT);
  const [sortDirection, setSortDirection] = useState(TOP_SORT_DIRECTION);
  const [legendItems, setLegendItems] = useState([]);
  const [range, setRange] = useState({ x: [], y: [] });
  const [modalRange, setModalRange] = useState({ x: [], y: [] });
  const [trace, setTrace] = useState([]);

  const resetRange = () => {
    setRange({ x: [], y: [] });
    setModalRange({ x: [], y: [] });
  };

  const setDefaultRange = () => {
    setRange({ x: [...DEFAULT_X_RANGE], y: [] });
  };

  const onRelayout = e => {
    const x = [e['xaxis.range[0]'], e['xaxis.range[1]']];
    const y = [e['yaxis.range[0]'], e['yaxis.range[1]']];

    if (
      !useDefaultRange && // Relayout always called after set default range so we skip this call if we're using default range
      (x.some(val => val !== undefined) ||
        y.some(val => val !== undefined) || // Some range is defined
        (e['xaxis.autorange'] && e['yaxis.autorange'])) // Autorange triggered (home button click or double click)
    ) {
      setRange({ x, y });
    } else {
      useDefaultRange = false;
    }
  };

  const onGraphUpdate = (_figure, graphDiv) => {
    const { shouldUpdate, newLegend } = shouldUpdateLegend(legendItems, graphDiv);
    if (shouldUpdate) {
      setLegendItems(newLegend);
    }
    if (
      (range.x[0] !== modalRange.x[0] && range.x[1] !== modalRange.x[1]) ||
      (range.y[0] !== modalRange.y[0] && range.y[1] !== modalRange.y[1])
    ) {
      setModalRange(range);
    }
  };

  const onInitialized = (figure, graphDiv) => {
    if (range.x.length === 0) {
      setDefaultRange();
    }
    onGraphUpdate(figure, graphDiv);
  };

  const onAfterPlot = () => {
    if (useDefaultRange && range.x[0] !== DEFAULT_X_RANGE[0] && range.x[1] !== DEFAULT_X_RANGE[1]) {
      setDefaultRange();
    }
  };

  const onLegendClick = data => {
    // get the index of the trace that was clicked
    const clickedIdx = data.curveNumber;
    const { text, visible } = data.fullData[clickedIdx];
    const clickedTrace = trace[clickedIdx];
    visibleTraces[text] = visible === true ? LEGEND_ONLY : true;
    clickedTrace.visible = visibleTraces[clickedTrace.text];
    trace[clickedIdx] = clickedTrace;
    const maxY = getMaxYFromTrace(trace, stacked);
    setRange({ x: range.x, y: [0, maxY] });
    setModalRange({ x: range.x, y: [0, maxY] });
    setTrace(trace);
  };

  // Currently used for all line charts since we limit the plotly legend to only 5 items
  // When we double click a trace in the legend the expected behavior is to hide all other traces
  // and only show the trace that was double clicked
  // The problem is since we only show 5 items, the default function only hides the other 4 traces
  // This function is used to toggle all the other traces
  const onLegendDoubleClick = data => {
    // get the index of the trace that was double clicked
    const clickedIdx = data.curveNumber;
    // if the trace that was double clicked is not visible then we want to show all
    // if the trace that was double clicked is visible then
    // a: hide all others if any others are visible
    // b: show all if all others are hidden
    const { visible } = data.fullData[clickedIdx];
    const hideAll =
      visible === true &&
      !!data.fullData.find((t, idx) => clickedIdx !== idx && t.visible === true);
    // toggle the traces
    const newTrace = trace.map((t, idx) => {
      const { text } = t;
      if (hideAll && clickedIdx !== idx) {
        visibleTraces[text] = LEGEND_ONLY;
        return { ...t, visible: LEGEND_ONLY };
      }
      visibleTraces[text] = true;
      return { ...t, visible: true };
    });
    setTrace(newTrace);
    if (hideAll) {
      setRange({ x: range.x, y: [0, trace[clickedIdx].y_max] });
      setModalRange({ x: range.x, y: [0, trace[clickedIdx].y_max] });
    } else {
      setDefaultRange();
    }
  };

  const { data, loading } = useQuery(GRAPH_ANALYTICS, {
    variables: {
      aggregate,
      checkboxAggregates: Object.keys(checkboxAggregates).filter(key => checkboxAggregates[key]),
      enableInfoOnClick: true,
      graphType: graphType.value,
      graphTypeOptions: graphTypeOptions.map(option => option.name),
      hydrocarbonType,
      portfolios,
      stacked,
      limit: parseInt(limit, 10),
      sortDirection: sortDirection.value,
      visibleTraces,
    },
  });

  const toggleTrace = traceName => {
    const clickedIdx = trace.findIndex(t => t.text === traceName);
    const newTrace = [...trace];
    const toggledTrace = { ...trace[clickedIdx] };
    toggledTrace.visible = toggledTrace.visible === true ? LEGEND_ONLY : true;
    visibleTraces[traceName] = toggledTrace.visible;
    newTrace[clickedIdx] = toggledTrace;
    const maxY = getMaxYFromTrace(newTrace, stacked);
    setRange({ x: range.x, y: [0, maxY] });
    setModalRange({ x: range.x, y: [0, maxY] });
    setTrace(newTrace);
  };

  const toggleLegendItem = traceName => {
    const idx = legendItems.findIndex(i => i.title === traceName);
    const newLegend = [...legendItems];
    const toggledItem = { ...legendItems[idx] };
    toggledItem.visible = !toggledItem.visible;
    newLegend[idx] = toggledItem;
    setLegendItems(newLegend);
  };

  const handleTraceToggle = traceName => {
    toggleTrace(traceName);
    toggleLegendItem(traceName);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetRange dependency
  useEffect(() => {
    resetRange();
    useDefaultRange = true;
    if (data?.graphAnalytics) {
      setTrace(cloneDeep(data.graphAnalytics.analytics || []));
    }
  }, [data]);

  const downloadCallback = (errorHandler, successHandler, dateType) => {
    const path = 'portfolio-analysis/export-asset-line-graph-data/';
    const body = {
      aggregate,
      checkboxAggregates: Object.keys(checkboxAggregates).filter(key => checkboxAggregates[key]),
      graphType: graphType.value,
      graphTypeOptions: graphTypeOptions.map(option => option.name),
      hydrocarbonType,
      portfolios,
      limit,
      dateType,
      sortDirection: sortDirection.value,
      visibleTraces,
    };
    const downloadName = `${portfolios[0].name}_${titleize(
      dateType,
    )}_Production_by_${aggregate}.xlsx`;

    downloadXlsx(path, body, downloadName, errorHandler, successHandler);
  };

  const title = `${graphType.label} ${graphTitles[hydrocarbonType]}`;

  return (
    <ProductionLineGraphContext.Provider
      value={{
        aggregate,
        downloadCallback,
        handleTraceToggle,
        onLegendClick,
        legendItems,
        loading,
        modalRange,
        onAfterPlot,
        onGraphUpdate,
        onInitialized,
        onLegendDoubleClick,
        onRelayout,
        range,
        setModalRange,
        setOpenControlsModal,
        setStacked,
        stacked,
        title,
        trace,
        maxY: data?.graphAnalytics?.maxY || [],
      }}
    >
      {children}
      <GraphControls
        aggregate={aggregate}
        aggregateTypes={lineGraphProductionAggregateTypes}
        checkboxAggregates={checkboxAggregates}
        googleAnalyticsTag={GOOGLE_ANALYTICS_TAG}
        graphType={graphType}
        graphTypeChildren={filteredLineGraphTypeChildren}
        graphTypeOptions={graphTypeOptions}
        hydrocarbonType={hydrocarbonType}
        includeOptions
        limit={limit}
        open={openControlsModal}
        setAggregate={setAggregate}
        setCheckboxAggregates={setCheckboxAggregates}
        setGraphType={setGraphType}
        setGraphTypeOptions={setGraphTypeOptions}
        setHydrocarbonType={setHydrocarbonType}
        setLimit={setLimit}
        setOpen={setOpenControlsModal}
        setSortDirection={setSortDirection}
        sortDirection={sortDirection}
        types={productionGraphTypes}
      />
    </ProductionLineGraphContext.Provider>
  );
};

export const ProductionLineGraphConsumer = ProductionLineGraphContext.Consumer;

export default ProductionLineGraphContext;
